import { CommonModule } from '@angular/common';
import { Component, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { AlertService } from '../../../../shared/services/alert.service';
import { LoadingService } from '../../../../shared/services/loading.service';

import { MatAutocomplete, MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { debounceTime, Subject, takeUntil } from 'rxjs';
import { BreadcrumbComponent } from '../../../../shared/components/breadcrumb/breadcrumb.component';
import {
  ConfirmationModalComponent
} from '../../../../shared/components/confirmation-modal/confirmation-modal.component';
import { FileService } from '../../../../shared/services/file.service';
import { UnitsService } from '../../../../shared/services/units.service';
import { UserService } from '../../../../shared/services/user.service';
import { IBreadcrumb } from '../../../../utils/interfaces/breadcrumb';
import { IProfile } from '../../../../utils/interfaces/profile';
import { IUnitList } from '../../../../utils/interfaces/unit';
import { IUser, IUserForm, IUserUnit } from '../../../../utils/interfaces/user';

export type emailFrequencyType = 0 | 1 | 2 | 3;

export interface IEmailFrequencyOption {
  value: emailFrequencyType;
  label: string;
}

@Component({
  selector: 'app-user',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    BreadcrumbComponent,
    MatInputModule,
    MatSelectModule,
    MatSlideToggleModule,
    MatAutocompleteModule,
    MatProgressBarModule,
    MatIconModule,
  ],
  providers: [FileService],
  templateUrl: './user.component.html',
  styleUrl: './user.component.scss',
})
export class UserComponent implements OnInit, OnDestroy {
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly fileService = inject(FileService);
  private readonly dialog = inject(MatDialog);
  private readonly router = inject(Router);
  private readonly unitsService = inject(UnitsService);
  private readonly userService = inject(UserService);
  private readonly loadingService = inject(LoadingService);
  private readonly alertService = inject(AlertService);

  private readonly unsubscribe$ = new Subject();

  @ViewChild('auto') autocomplete!: MatAutocomplete;

  breadcrumb: IBreadcrumb[] = [];

  form!: FormGroup<IUserForm>;
  user?: IUser;

  unitSearch = new FormControl<string | null>(null);

  unitsList: IUnitList[] = [];

  profiles: IProfile[] = [];

  loadingUnits = false;
  hasMoreUnits = false;
  currentPage = 1;
  pageSize = 20;
  totalUnits = 0;
  isDropdownOpen = false;
  private savedScrollPosition = 0;

  emailFrequencyOptions: IEmailFrequencyOption[] = [
    { value: 0, label: 'Nenhum' },
    { value: 1, label: 'Diário' },
    { value: 2, label: 'Semanal' },
    { value: 3, label: 'Mensal' },
  ];

  private previousReservationDishValues: number[] = [];
  private previousEmailFrequencyValues: number[] = [];


  ngOnInit(): void {
    this.user = this.activatedRoute.snapshot.data['user']?.data;
    this.profiles = this.activatedRoute.snapshot.data['profiles']?.data;

    this.initForm();

    this.handleEmailFrequencyListChanges();
    this.handleReservationDishEmailFrequencyListChanges();

    this.handleUniSearchChanges();

    if (this.user) {
      this.form.setControl('id', new FormControl<string | null>(this.user.id));
      this.breadcrumb = [
        {
          description: 'Usuários',
          url: 'main/registration/users',
          askBeforeChangingRoute: true,
          textTitle: 'Editar usuário',
          descriptionMsg:
            'Deseja realmente sair da página de edição? As alterações não salvas serão perdidas.',
        },
        {
          description: 'Editar usuário',
          url: '',
        },
      ];
    } else {
      this.breadcrumb = [
        {
          description: 'Usuários',
          url: 'main/registration/users',
          askBeforeChangingRoute: true,
          textTitle: 'Novo usuário',
          descriptionMsg:
            'Deseja realmente sair da página de cadastro? As alterações não salvas serão perdidas.',
        },
        {
          description: 'Novo usuário',
          url: '',
        },
      ];
    }

  }

  ngOnDestroy(): void {
    this.unsubscribe$.next(null);
    this.unsubscribe$.complete();
  }

  emailMatchValidator(control: FormControl): { [key: string]: boolean } | null {
    const email = this.form?.controls['email'].value;
    return control.value === email ? null : { emailsMismatch: true };
  }

  addUnit(unit: IUnitList): void {
    const units = this.form.controls['branchesUser'].value ?? [];

    if (units.some((u) => u.branchId === unit.id)) {
      return;
    }

    units.push({ branchId: unit.id, name: unit.name });

    this.form.controls['branchesUser'].setValue(units);
  }

  openDropdown(): void {
    this.isDropdownOpen = true;
    if (this.unitsList.length === 0) {
      this.currentPage = 1;
      this.getUnits('', false);
    }
  }

  onInputBlur(event: FocusEvent): void {
    setTimeout(() => {
      const relatedTarget = event.relatedTarget as HTMLElement;
      if (!relatedTarget || !relatedTarget.closest('.custom-dropdown-panel')) {
        this.isDropdownOpen = false;
      }
    }, 200);
  }

  selectUnit(unit: IUnitList): void {
    this.addUnit(unit);
    this.unitSearch.setValue('', { emitEvent: false });
  }

  onDropdownScroll(event: Event): void {
    const target = event.target as HTMLElement;
    const scrollTop = target.scrollTop;
    const scrollHeight = target.scrollHeight;
    const clientHeight = target.clientHeight;

    this.savedScrollPosition = scrollTop;

    const isNearBottom = (scrollTop + clientHeight) >= (scrollHeight - 50);

    if (isNearBottom && this.hasMoreUnits && !this.loadingUnits) {
      this.loadMoreUnits();
    }
  }

  private loadMoreUnits(): void {
    if (!this.hasMoreUnits || this.loadingUnits) {
      return;
    }

    this.currentPage++;
    const searchValue = this.unitSearch.value || '';
    this.getUnits(searchValue, false, true);
  }

  isUnitSelected(unitId: string): boolean {
    const units = this.form.controls['branchesUser'].value ?? [];
    return units.some((u) => u.branchId === unitId);
  }

  selectAllUnits(event: Event): void {
    event.preventDefault();
    event.stopPropagation();

    const dialogRef = this.dialog.open(ConfirmationModalComponent, {
      width: '500px',
      data: {
        title: 'Selecionar todas as unidades',
        description: 'Deseja realmente selecionar todas as unidades disponíveis?',
      },
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((result) => {
        if (result) {
          this.loadAllUnitsAndSelect();
        }
      });
  }

  private loadAllUnitsAndSelect(): void {
    this.loadingService.showLoading();

    this.unitsService
      .getUnitsFilter(false, '', 1, 999999, true)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: (response) => {
          const currentUnits = this.form.controls['branchesUser'].value ?? [];
          const allUnits = response.data;
          const newUnits = allUnits
            .filter(unit => !currentUnits.some(u => u.branchId === unit.id))
            .map(unit => ({ branchId: unit.id, name: unit.name }));

          const updatedUnits = [...currentUnits, ...newUnits];

          this.form.controls['branchesUser'].setValue(updatedUnits);

          this.loadingService.closeLoading();
          this.alertService.success(`${newUnits.length} unidade(s) adicionada(s) com sucesso!`);
        },
        error: () => {
          this.loadingService.closeLoading();
          this.alertService.danger('Erro ao carregar todas as unidades.');
        }
      });
  }

  deleteUnit(id: string): void {
    const units = this.form.controls['branchesUser'].value ?? [];

    const dialogRef = this.dialog.open(ConfirmationModalComponent, {
      width: '500px',
      data: {
        title: 'Excluir unidade',
        description: 'Deseja realmente excluir esta unidade?',
      },
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((result) => {
        if (result)
          this.form.controls['branchesUser'].setValue(
            units.filter((unit) => unit.branchId !== id)
          );
      });
  }

  getUnits(filter: string | null, search = false, append = false): void {
    if (!filter && search) {
      this.unitsList = [];
      this.currentPage = 1;
      this.hasMoreUnits = false;
      return;
    }

    this.loadingUnits = true;

    this.unitsService
      .getUnitsFilter(search, filter || '', this.currentPage, this.pageSize, true)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: (response) => {
          const newUnits = response.data;
          this.totalUnits = response.count ?? 0;

          console.log('Resposta recebida:', { 
            novosItens: newUnits.length, 
            totalUnits: this.totalUnits, 
            listaAtual: this.unitsList.length,
            append 
          });

          if (append) {
            this.unitsList = [...this.unitsList, ...newUnits];
          } else {
            this.unitsList = newUnits;
          }

          this.hasMoreUnits = this.unitsList.length < this.totalUnits;

          this.loadingUnits = false;
        },
        error: () => {
          this.loadingUnits = false;
        }
      });
  }

  cancel(): void {
    const data: IBreadcrumb = this.breadcrumb[0];

    const dialogRef = this.dialog.open(ConfirmationModalComponent, {
      width: '500px',
      data: {
        title: data.textTitle,
        description: data.descriptionMsg,
      },
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((result) => {
        if (result) this.router.navigate([data.url]);
      });
  }

  handlerSaveUser(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.loadingService.showLoading();
    if (!this.user) {
      this.createUser(this.form.getRawValue() as IUser);
    } else {
      this.updateUser(this.form.getRawValue() as IUser);
    }
  }

  createUser(user: IUser): void {
    this.userService
      .createUser(user)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: () => {
          this.alertService.success('Usuário criado com sucesso!');
          this.router.navigate(['main/registration/users']);
        },
      });
  }

  updateUser(user: IUser): void {
    this.userService
      .updateUser(user)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: () => {
          this.alertService.success('Usuário atualizado com sucesso!');
          this.router.navigate(['main/registration/users']);
        },
      });
  }

  uploadImage(file: File): void {
    this.fileService
      .uploadImage(file)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((res) => {
        this.form.controls.photo.setValue(res.data);
        this.loadingService.closeLoading();
      });
  }

  resizeImage(event: Event): void {
    const target = event.target as HTMLInputElement;
    const file = (target.files as FileList)[0];

    if (!file) return;

    this.loadingService.showLoading();

    this.fileService
      .resizeImage(file)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((newFile) => {
        this.uploadImage(newFile);
      });
  }

  private initForm(): void {
    this.form = new FormGroup<IUserForm>(<IUserForm>{
      name: new FormControl<string | null>(
        this.user?.name ?? null,
        Validators.required
      ),
      email: new FormControl<string | null>(
        {
          value: this.user?.email ?? null,
          disabled: this.user?.loginMicrosoft || false,
        },
        [Validators.required, Validators.email]
      ),
      confirmEmail: new FormControl<string | null>(
        {
          value: this.user?.email ?? null,
          disabled: this.user?.loginMicrosoft || false,
        },
        [Validators.required, this.emailMatchValidator.bind(this)]
      ),
      active: new FormControl<boolean>(this.user?.active ?? false),
      roleId: new FormControl<string | null>(
        this.user?.role?.id ?? null,
        Validators.required
      ),
      emailFrequencyList: new FormControl<number[]>(
        this.user?.emailFrequencyList.length
          ? this.user?.emailFrequencyList
          : [this.emailFrequencyOptions[0].value],
        Validators.required
      ), reservationDishEmailFrequencyList: new FormControl<number[]>(
        this.user?.reservationDishEmailFrequencyList.length
          ? this.user?.reservationDishEmailFrequencyList
          : [this.emailFrequencyOptions[0].value],
        Validators.required
      ),
      branchesUser: new FormControl<IUserUnit[] | null>(
        this.user?.branchesByUser?.map((branch) => {
          return { branchId: branch.id!, name: branch.name };
        }) ?? [],
        Validators.required
      ),
      photo: new FormControl<string | null>(this.user?.photo ?? ''),
    });

    this.previousReservationDishValues = this.form.controls.reservationDishEmailFrequencyList.value ?? [];
    this.previousEmailFrequencyValues = this.form.controls.emailFrequencyList.value ?? [];

  }

  private handleUniSearchChanges(): void {
    this.unitSearch.valueChanges
      .pipe(takeUntil(this.unsubscribe$))
      .pipe(debounceTime(700))
      .subscribe((value) => {
        this.currentPage = 1;
        this.getUnits(value, true, false);
      });
  }

  private handleEmailFrequencyListChanges(): void {
    this.form.controls.emailFrequencyList.valueChanges
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((values) => {
        if (!values) return;
        if (
          this.previousEmailFrequencyValues.includes(0) &&
          values.some(v => v !== 0)
        ) {
          const valueNoZero = values.filter(v => v !== 0);
          this.previousEmailFrequencyValues = valueNoZero;
          this.form.controls.emailFrequencyList.setValue(valueNoZero, { emitEvent: false });

          return;
        }

        if (values.includes(0) && values.length > 1) {
          this.form.controls.emailFrequencyList.setValue([0], { emitEvent: false });
          this.previousEmailFrequencyValues = [0];
          return;
        }

        this.previousEmailFrequencyValues = values;
      });
  }

  private handleReservationDishEmailFrequencyListChanges(): void {
    this.form.controls.reservationDishEmailFrequencyList.valueChanges
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((values) => {
        if (!values) return;
        if (
          this.previousReservationDishValues.includes(0) &&
          values.some(v => v !== 0)
        ) {
          const valueNoZero = values.filter(v => v !== 0);
          this.previousReservationDishValues = valueNoZero;
          this.form.controls.reservationDishEmailFrequencyList.setValue(valueNoZero, { emitEvent: false });

          return;
        }

        if (values.includes(0) && values.length > 1) {
          this.form.controls.reservationDishEmailFrequencyList.setValue([0], { emitEvent: false });
          this.previousReservationDishValues = [0];
          return;
        }

        this.previousReservationDishValues = values;
      });
  }
}